import { Icon } from '@blueprintjs/core';
import React from 'react'

interface ISOFTableProps {
	reportItem: any;
	tableType: "statementOfFactsTable" | "cargoHandlingTable";
	onClose: () => void;
}

function SOFTable(props: ISOFTableProps) {
	const { reportItem, tableType, onClose } = props;
	return (
		<div className='bg-white p-4 pb-8 rounded-lg'>
			{tableType === "statementOfFactsTable" ? (
				<div className='flex flex-col gap-4'>
					<div className='flex justify-between items-center bg-blue-100 p-2 rounded'>
						<h2 className='text-lg font-medium text-blue-500'>Statement of Facts</h2>
						<button
							className='text-gray-500 hover:text-gray-700 flex items-center'
							onClick={onClose}
						>
							<Icon icon="cross" />
						</button>
					</div>
					<div className='flex flex-col gap-2'>
						<h3>Port Activity Template:</h3>
						<div className='border text-blue-500 bg-blue-100 rounded w-fit px-2'>
							{reportItem?.sof_port_activity_template}
						</div>
					</div>

					<div className='overflow-y-scroll max-h-[80vh]'>
						<table className='w-full border'>
							<thead className='sticky -top-1'>
								<tr>
									<th className='border px-4 py-2 text-left text-gray-800'>Activity</th>
									<th className='border px-4 py-2 text-left text-gray-800'>Cargo</th>
									<th className='border px-4 py-2 text-left text-gray-800'>Remarks</th>
									<th className='border px-4 py-2 text-left text-gray-800'>Date & Time (Timezone)</th>
								</tr>
							</thead>
							<tbody>
								{reportItem?.statementOfFactsTable?.map((sofItem: any, index: number) => (
									<tr key={index}>
										<td className='border px-4 py-2 text-gray-700'>{sofItem?.sof_table_activity}</td>
										<td className='border px-4 py-2 text-gray-700'>{sofItem?.sof_table_cargo?.label}</td>
										<td className='border px-4 py-2 text-gray-700'>{sofItem?.sof_table_remarks}</td>
										<td className='border px-4 py-2 text-gray-700'>
											{sofItem?.sof_table_date.split("T")[0]} at {" "}
											{sofItem?.sof_table_date.split("T")[1]}{" "}
											<span className='text-gray-500'>{sofItem?.sof_table_zd && `(${sofItem?.sof_table_zd})`}</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			) : (
				<div className='flex flex-col gap-4'>
					<div className='flex justify-between items-center bg-blue-100 p-2 rounded'>
						<h2 className='text-lg font-medium text-blue-500'>Cargo Handling</h2>
						<button
							className='text-gray-500 hover:text-gray-700 flex items-center'
							onClick={onClose}
						>
							<Icon icon="cross" />
						</button>
					</div>
					<div className='overflow-y-scroll max-h-[80vh]'>
						<table className='min-w-full border'>
							<thead className='sticky -top-1'>
								<tr>
									<th className='border px-4 py-2 text-left text-gray-800'>Cargo</th>
									<th className='border px-4 py-2 text-left text-gray-800'>L/D</th>
									<th className='border px-4 py-2 text-left text-gray-800'>BL Code</th>
									<th className='border px-4 py-2 text-left text-gray-800'>BL/Disch Date</th>
									<th className='border px-4 py-2 text-left text-gray-800'>Unit</th>
									<th className='border px-4 py-2 text-left text-gray-800'>BL Gross</th>
									<th className='border px-4 py-2 text-left text-gray-800'>Ship Gross</th>
								</tr>
							</thead>
							<tbody>
								{reportItem?.cargoHandlingTable?.map((cargoItem: any, index: number) => (
									<>
										<tr key={index + cargoItem?.table_cargo?.label}>
											<td className='border px-4 py-2'>{cargoItem?.table_cargo?.label}</td>
											<td className='border px-4 py-2'>{cargoItem?.table_ld}</td>
											<td className='border px-4 py-2'>{cargoItem?.table_bl_code}</td>
											<td className='border px-4 py-2'>{cargoItem?.table_bl_disch_date}</td>
											<td className='border px-4 py-2'>{cargoItem?.table_unit}</td>
											<td className='border px-4 py-2'>{cargoItem?.table_bl_gross}</td>
											<td className='border px-4 py-2'>{cargoItem?.table_ship_gross}</td>
										</tr>

										{/* Alternate values */}
										<tr key={`${index}-alternative`}>
											<td colSpan={4} className='border px-4 py-2 text-right'>Alternative unit figures:</td>
											<td className='border px-4 py-2'>{cargoItem?.table_alternate_unit}</td>
											<td className='border px-4 py-2'>{cargoItem?.table_alternate_bl_gross}</td>
											<td className='border px-4 py-2'>{cargoItem?.table_alternate_ship_gross}</td>
										</tr>

										<tr key={`${index}-extra-l-temp`} className='bg-gray-100'>
											<td className='border px-4 py-2 text-right'>
												L Temp:
											</td>
											<td colSpan={7}><span className='px-4 py-2'>{cargoItem?.table_l_temp}</span></td>
										</tr>
										<tr key={`${index}-extra-api`} className='bg-gray-100'>
											<td className='border px-4 py-2 text-right'>
												API:
											</td>
											<td colSpan={7}><span className='px-4 py-2'>{cargoItem?.table_api}</span></td>
										</tr>
										<tr key={`${index}-extra-charterer`} className='bg-gray-100'>
											<td className='border px-4 py-2 text-right'>
												Charterer:
											</td>
											<td colSpan={7}><span className='px-4 py-2'>{cargoItem?.table_charterer}</span></td>
										</tr>
										<tr key={`${index}-extra-consignee`} className='bg-gray-100'>
											<td className='border px-4 py-2 text-right'>
												Consignee:
											</td>
											<td colSpan={7}><span className='px-4 py-2'>{cargoItem?.table_consignee}</span></td>
										</tr>
										<tr key={`${index}-extra-receiver`} className='bg-gray-100'>
											<td className='border px-4 py-2 text-right'>
												Receiver:
											</td>
											<td colSpan={7}><span className='px-4 py-2'>{cargoItem?.table_receiver}</span></td>
										</tr>
										<tr key={`${index}-extra-shipper`} className='bg-gray-100'>
											<td className='border px-4 py-2 text-right'>
												Shipper:
											</td>
											<td colSpan={7}><span className='px-4 py-2'>{cargoItem?.table_shipper}</span></td>
										</tr>
										<tr key={`${index}-extra-destination`} className='bg-gray-100'>
											<td className='border px-4 py-2 text-right'>
												Destination:
											</td>
											<td colSpan={7}><span className='px-4 py-2'>{cargoItem?.table_destination}</span></td>
										</tr>
										<tr key={`${index}-extra-letter-of-protest`} className='bg-gray-100'>
											<td className='border px-4 py-2 text-right'>
												Letter of Protest:
											</td>
											<td colSpan={7}><span className='px-4 py-2'>{cargoItem?.table_letter_of_protest}</span></td>
										</tr>
									</>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}
		</div>
	)
}

export default SOFTable